import {
  lazy,
  useMemo,
  useState,
  useEffect,
  useCallback,
  Fragment,
  ReactNode,
  Suspense,
} from 'react'
import { Link as RouterLink, useLocation } from 'react-router-dom'
import { AppBar } from '@mui/material'
import MuiAvatar from '@mui/material/Avatar'
import Grid from '@mui/material/Grid'
import Link from '@mui/material/Link'
import MenuItem from '@mui/material/MenuItem'
import { EVENTS, CATEGORIES } from '@common/constants/tracking'
import { TEXT_ROLE_CASING, formatCase } from '@common/helpers/casing'
import { WorkflowTypes } from '@common/interfaces/notes'
import { PhotoSubjectType } from '@common/interfaces/photo'
import { IAimStatus, UserRole } from '@common/interfaces/user'
import cn from 'classnames'
import GlobalSearch from '../../components/GlobalSearch'
import { ROUTES, isBlankLayoutRoute } from '../../constants/routes'
import { setBaseTitle } from '../../helpers/documentTitle'
import {
  NotesPanelPreference,
  NotesPanelState,
  isNotesColumnSuppressed,
  setNotesPanelPreference,
} from '../../helpers/notesPanel'
import { usePermissions } from '../../helpers/permissionContext'
import { ATLAS_PREVIEW_OPT_OUT } from '../../hooks/useGlobalAtlasLinkPreview'
import { useRouteTitle } from '../../hooks/useRouteTitle'
import AimStatusMenuItem from '../AimStatusModal/AimStatusMenuItem'
import { useOwnAimStatus } from '../AimStatusModal/useOwnAimStatus'
import ColorModeToggle from '../ColorModeToggle'
import AimStatusEmoji from '../Common/AimStatus'
import Avatar from '../Common/Avatar'
import BackTrail from '../Common/BackTrail'
import CopyPageLink from '../Common/CopyPageLink'
import EditableProfilePhoto from '../Common/EditableProfilePhoto'
import Icon from '../Common/Icon'
import Menu, { OpenMenu } from '../Common/Menu'
import { setPageTitleActionsSlot } from '../Common/PageTitleActions'
import ProfilePhoto from '../Common/ProfilePhoto'
import Text from '../Common/Text'
import NotificationPane from '../NotificationPane'
import styles from './Header.module.scss'
import { PropsFromRedux } from './index'
import { titles } from './titles'

// Opened from the account menu; it carries the emoji picker, so it loads on first open.
const AimStatusModal = lazy(() => import('../AimStatusModal/AimStatusModal'))

export interface IProps extends PropsFromRedux {
  workflow: string
  /** Accepted so MainLayout can pass it to either header; internal always has a side menu. */
  hasSideMenu?: boolean
}

const HeaderUserActions = ({
  isInternalUser,
  isMessagingUser,
  aimStatus,
  renderTrigger,
  handleLogout,
  onOpenStatusModal,
}: {
  isInternalUser: boolean
  isMessagingUser: boolean
  aimStatus: IAimStatus | null
  renderTrigger: (open: OpenMenu, isOpen: boolean) => ReactNode
  handleLogout: () => void
  onOpenStatusModal: () => void
}) => {
  const [closeMenuCount, setCloseMenuCount] = useState(0)

  const handleCloseMenu = useCallback(() => {
    setCloseMenuCount((prev) => prev + 1)
  }, [])

  // Close the dropdown as the dialog opens, or the menu paper sits over it.
  const handleOpenStatusModal = useCallback(() => {
    handleCloseMenu()
    onOpenStatusModal()
  }, [handleCloseMenu, onOpenStatusModal])

  return (
    <Menu
      paperClassName={styles.actionsMenu}
      closeMenuCount={closeMenuCount}
      renderTrigger={renderTrigger}
      transformOrigin={{
        vertical: -10,
        horizontal: 'left',
      }}
    >
      {isMessagingUser && (
        <AimStatusMenuItem
          status={aimStatus}
          className={styles.actionsMenuItem}
          onClick={handleOpenStatusModal}
        />
      )}
      {isInternalUser && (
        <Link className={styles.actionsMenuItemLink} component={RouterLink} to={ROUTES.MY_FEEDBACK}>
          <MenuItem
            classes={{
              root: styles.actionsMenuItem,
            }}
            onClick={handleCloseMenu}
          >
            Feedback
          </MenuItem>
        </Link>
      )}
      <Link
        className={styles.actionsMenuItemLink}
        component={RouterLink}
        to={ROUTES.SETTINGS_PROFILE}
      >
        <MenuItem
          classes={{
            root: styles.actionsMenuItem,
          }}
          onClick={handleCloseMenu}
        >
          Settings
        </MenuItem>
      </Link>
      <MenuItem
        classes={{
          root: styles.actionsMenuItem,
        }}
        onClick={handleLogout}
      >
        Sign out
      </MenuItem>
    </Menu>
  )
}

const Header = ({
  toggleNotes,
  user,
  workflow,
  role,
  isSidebarOpen,
  title,
  titleCasing,
  pageLogo,
  pageLogoSubject,
  pageParent,
  pageSubtitle,
  updatePhoto,
  deletePhoto,
  onLogout,
  ownAimStatus,
  updateAimProfile,
  isAdminLoggedAsClient,
  clientInfo,
  notesPanelState,
  trackVisualizations,
}: IProps) => {
  const [pageTitle, setPageTitle] = useState('')

  // The header's editable logo dispatches the generic photo actions straight from the subject's
  // type and id — the reason the subject in redux needs no handlers of its own, and the reason one
  // header works for every subject rather than each page wiring its own upload path.
  const handleUploadPageLogo = useCallback(
    (file: File) =>
      pageLogoSubject
        ? updatePhoto(pageLogoSubject.subjectType, pageLogoSubject.id, file)
        : Promise.resolve(),
    [pageLogoSubject, updatePhoto],
  )

  const handleRemovePageLogo = useCallback(
    () =>
      pageLogoSubject
        ? deletePhoto(pageLogoSubject.subjectType, pageLogoSubject.id)
        : Promise.resolve(),
    [pageLogoSubject, deletePhoto],
  )

  // The header notes button is the one deliberate "change my default" gesture,
  // so it persists the choice per workflow. Cell/flag deep-links and the
  // resolver use non-persisting paths.
  const handleToggleNotes = useCallback(() => {
    // Typed as a *preference*, not a panel state: 'hidden' is a per-page
    // suppression and must never be written as the user's remembered choice.
    const next: NotesPanelPreference =
      notesPanelState === NotesPanelState.docked ? NotesPanelState.rail : NotesPanelState.docked
    if (workflow) {
      setNotesPanelPreference(workflow as WorkflowTypes, next)
    }
    void trackVisualizations(
      next === NotesPanelState.docked ? EVENTS.notesOpen : EVENTS.notesCollapse,
      CATEGORIES.notesSidebar,
      { data: { workflow, source: 'header' } },
    )
    toggleNotes()
  }, [notesPanelState, workflow, toggleNotes, trackVisualizations])

  const { isParticipant, isInternalUser, isMessagingUser } = usePermissions()
  const { pathname } = useLocation()

  const routeTitle = useRouteTitle(titles)

  useEffect(() => {
    if (routeTitle) {
      setPageTitle(routeTitle)
      setBaseTitle(routeTitle)
    } else {
      setPageTitle(title)
    }
  }, [routeTitle, title, setPageTitle])

  const displayTitle = formatCase(routeTitle ? TEXT_ROLE_CASING.pageTitle : titleCasing, pageTitle)

  // Global search (and the notes rail with it) reaches across the whole
  // portfolio, so it is withheld from the tasks-and-messaging role for the same
  // reason the pages it searches are. Tasks have their own search on the Tasks
  // page, which is scoped to tasks.
  const isNotesAndSearchUser = useMemo(
    () => ![UserRole.CLIENT_USER, UserRole.TASKS_AND_MESSAGING].includes(role),
    [role],
  )

  // MainLayout skips mounting the column on this page for this role; without the
  // same check here the toggle stays, flipping state nothing reads.
  const isNotesSuppressed = useMemo(() => isNotesColumnSuppressed(role, workflow), [role, workflow])

  const isNotificationsAvailable = useMemo(() => ![UserRole.CLIENT_USER].includes(role), [role])

  const {
    liveStatus,
    isStatusModalOpen,
    handleOpenStatusModal,
    handleCloseStatusModal,
    handleSaveStatus,
  } = useOwnAimStatus(ownAimStatus, updateAimProfile)

  const handleLogout = useCallback(async () => {
    await onLogout()
  }, [onLogout])

  const userName = useMemo(
    // Nickname first, as everywhere else in-app; the legal first name is the
    // fallback for the rows that predate it.
    () =>
      isAdminLoggedAsClient
        ? clientInfo?.clientName || ''
        : user?.nickname?.trim() || user?.firstName,
    [isAdminLoggedAsClient, user, clientInfo],
  )

  const isBlankLayout = useMemo(() => isBlankLayoutRoute(pathname), [pathname])

  const renderUserTrigger = useCallback(
    (open?: OpenMenu, isOpen?: boolean) => (
      <div className={styles.user} onClick={open}>
        {isInternalUser && !isAdminLoggedAsClient ? (
          /* ProfilePhoto rather than the Avatar adapter: this is the one
             avatar showing the viewer their own account, and it is the
             renderer the rest of the app resolves photos through. */
          <ProfilePhoto
            name={userName}
            subjectType={PhotoSubjectType.Users}
            id={user.id}
            photo={user.avatar}
            size="large"
          />
        ) : (
          <Avatar user={user} clientInfo={isAdminLoggedAsClient ? clientInfo : null} />
        )}
        <Text
          variant="title"
          tone="primary"
          component="span"
          casing="none"
          data-cy="header-username"
        >
          Hello, {userName}
        </Text>
        {/* Your own status, next to your own name — the same placement
            everyone else's gets, so the header shows you what colleagues
            see rather than a private variant of it.
            `statusSource`, not `liveStatus`: the latter is manual-only
            (`ownAimStatus` reads the three manual columns), so a
            calendar-derived status showed on every teammate's screen and
            not on your own. Handing over the user row lets this resolve
            through the same manual-then-calendar precedence the server
            applies. The modal below deliberately keeps `liveStatus` — see
            its `currentStatus`. */}
        {isMessagingUser && <AimStatusEmoji userId={user.id} statusSource={user} />}
        {!isAdminLoggedAsClient && (
          <Icon
            name="chevron-down"
            size="sm"
            className={cn({
              [styles.rotate]: isOpen,
            })}
          />
        )}
      </div>
    ),
    [isInternalUser, isMessagingUser, isAdminLoggedAsClient, userName, user, clientInfo],
  )

  if (!user) {
    return null
  }
  return (
    <AppBar
      {...ATLAS_PREVIEW_OPT_OUT}
      className={cn(styles.appBar, {
        [styles.loggedAsClientAppBar]: isAdminLoggedAsClient,
      })}
    >
      <Grid container className={styles.appBarContainer} size={12}>
        <Grid size={4}>
          <div
            className={cn(styles.title, {
              [styles.titleExpanded]: isSidebarOpen,
            })}
            id="pageTitle"
          >
            <BackTrail />
            {pageLogoSubject?.isReadOnly ? (
              <ProfilePhoto
                subjectType={pageLogoSubject.subjectType}
                id={pageLogoSubject.id}
                name={pageLogoSubject.name}
                photo={pageLogoSubject.photo}
                iconUrl={pageLogoSubject.iconUrl}
                size="large"
                className={styles.pageLogo}
                data-cy="page-logo"
              />
            ) : pageLogoSubject ? (
              /* A subject wins over a plain pageLogo URL: it renders the subject's own
                 ProfilePhoto, so it repaints from the photo cache the moment the photo changes
                 anywhere on the page, and it opens the shared editor on click. */
              <EditableProfilePhoto
                subjectType={pageLogoSubject.subjectType}
                id={pageLogoSubject.id}
                name={pageLogoSubject.name}
                photo={pageLogoSubject.photo}
                iconUrl={pageLogoSubject.iconUrl}
                editorTitle={pageLogoSubject.editorTitle}
                profileUrl={pageLogoSubject.profileUrl}
                size="large"
                className={styles.pageLogo}
                data-cy="page-logo"
                onUpload={handleUploadPageLogo}
                onRemove={handleRemovePageLogo}
              />
            ) : (
              pageLogo && (
                <MuiAvatar
                  src={pageLogo}
                  alt=""
                  variant="rounded"
                  className={styles.pageLogo}
                  data-cy="page-logo"
                />
              )
            )}
            <span className={styles.titleStack}>
              <span className={styles.titleLine}>
                {pageParent && (
                  <>
                    <Link
                      component={RouterLink}
                      to={pageParent.link}
                      className={styles.parentLink}
                      title={pageParent.title}
                      data-cy="page-parent-link"
                    >
                      {pageParent.title}
                    </Link>
                    <span className={styles.parentSeparator} aria-hidden="true">
                      /
                    </span>
                  </>
                )}
                <span className={styles.titleText} title={displayTitle}>
                  {displayTitle}
                </span>
              </span>
              {pageSubtitle && (
                <span className={styles.subtitleText} title={pageSubtitle}>
                  {pageSubtitle}
                </span>
              )}
            </span>
            <div ref={setPageTitleActionsSlot} className={styles.titleActions} />
            <CopyPageLink />
          </div>
        </Grid>
        <Grid className={styles.centerSlot} size={4}>
          {!isBlankLayout && isNotesAndSearchUser && (
            <div className={styles.searchBar}>
              <GlobalSearch />
            </div>
          )}
        </Grid>

        <Grid className={styles.rightSlot} size={4}>
          <ColorModeToggle />
          {isNotificationsAvailable && <NotificationPane />}
          {user && (
            <Fragment>
              {/* Logged in as a client there is no user menu, so the same block
                  renders as a plain, inert header row rather than a trigger. */}
              {isAdminLoggedAsClient ? (
                renderUserTrigger()
              ) : (
                <HeaderUserActions
                  isInternalUser={isInternalUser}
                  isMessagingUser={isMessagingUser}
                  aimStatus={liveStatus}
                  renderTrigger={renderUserTrigger}
                  handleLogout={handleLogout}
                  onOpenStatusModal={handleOpenStatusModal}
                />
              )}

              {isStatusModalOpen && (
                <Suspense fallback={null}>
                  <AimStatusModal
                    open
                    /* Manual-only, unlike the emoji above: prefilling the form with
                     a calendar-derived status would save it back as one the user
                     typed, which no sweep could then correct. */
                    currentStatus={liveStatus}
                    onClose={handleCloseStatusModal}
                    onSave={handleSaveStatus}
                  />
                </Suspense>
              )}
            </Fragment>
          )}
          {workflow && isNotesAndSearchUser && !isParticipant && !isNotesSuppressed && (
            <div className={cn(styles.toggleNotes, 'mui-fixed')}>
              <Icon name="activity-bars" size="lg" onClick={handleToggleNotes} />
            </div>
          )}
        </Grid>
      </Grid>
    </AppBar>
  )
}

export default Header
